// Шар розділу /scada/*. Використовує DashboardLayout (сайдбар, топбар, гейт
// авторизації) і додає темну поверхню + ЄДИНЕ підменю SCADA.
//
// Архітектура навігації:
//  · склад і порядок пунктів — у src/scada/navConfig.ts, одне джерело правди;
//  · ряд рендериться ТІЛЬКИ тут, тож він однаковий на картці станції та на
//    сторінках розділів — жодної конкуруючої навігації всередині сторінок;
//  · активний пункт обчислюється з МАРШРУТУ (activeNavKey), а не з пропа й не
//    з локального стану сторінки;
//  · вкладки станції — теж маршрути (/scada/station/:id/:tab), тож F5,
//    Back/Forward і прямі посилання працюють однаково для всіх десяти пунктів.
//
// Вигляд: icon-only 40×40 (SegmentedTabs variant="outline" iconOnly), підпис —
// у tooltip. Склад ряду сталий (10 пунктів), тож між екранами він не стрибає.
//
// «УЗЕ» вимикається лише там, де бекенд каже, що накопичувача немає
// (StationTelemetry.hasBess — довідник asset_bess_systems). Статичний
// STATIONS[].hasBess більше не вживається: він був третім, розбіжним
// джерелом і саме через нього вкладка відкривалась порожньою.
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from './DashboardLayout';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import { scada } from '@/scada/tokens';
import StationIdentity from './StationIdentity';
import StationStatusStrip from '@/components/scada/StationStatusStrip';
import { STATIONS } from '@/scada/mock/stations';
import { useScadaStation } from '@/scada/useScadaStation';
import { useStationTelemetry } from '@/scada/useTelemetry';
import {
  STATION_TAB_ITEMS, SUBNAV_SECTION_KEYS, activeNavKey, isStationTab,
  parseStationPath, scadaTitle, sectionByKey, stationPath,
} from '@/scada/navConfig';
import { hasArtemKView } from '@/utils/artemkView';

// Пропів НЕМАЄ навмисно: сторінки не можуть підсунути в каркас власну шапку.
// Усе, що вище контенту, належить цьому компоненту, а сторінки — це <Outlet />.
export default function ScadaLayout() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const station = useScadaStation();
  const { user } = useAuth();

  // Станція, до якої ведуть вкладки: з адреси, якщо ми вже на картці, інакше —
  // з глобального селектора. Один контекст на весь розділ.
  const fromUrl = parseStationPath(location.pathname);
  const stationId = fromUrl?.id ?? station.id;
  // Той самий спільний SSE-потік, що й у сторінок: додаткових запитів немає.
  const { data: telemetry } = useStationTelemetry(stationId ?? '');
  const cfg = STATIONS.find((s) => s.id === stationId);

  const activeKey = activeNavKey(location.pathname);

  const items = [
    ...STATION_TAB_ITEMS.map((it) => ({
      key: `tab:${it.key}`,
      label: it.label,
      Icon: it.Icon,
      // Вимикаємо, лише коли ТОЧНО знаємо, що накопичувача немає: поки
      // телеметрія не приїхала, пункт лишається доступним.
      disabled: !stationId || (!!it.needsBess && !!telemetry && !telemetry.hasBess),
      title: it.needsBess && !!telemetry && !telemetry.hasBess
        ? t('ak_nav_no_energy_storage_at_this_solar_plant', { label: it.label })
        : undefined,
    })),
    ...SUBNAV_SECTION_KEYS.map((k) => sectionByKey(k)!).map((s) => ({
      key: `nav:${s.key}`,
      label: s.label,
      Icon: s.Icon,
    })),
  ];

  const onSelect = (key: string) => {
    if (key.startsWith('tab:')) {
      const tab = key.slice(4);
      if (!stationId || !isStationTab(tab)) return;
      navigate(stationPath(stationId, tab));
      return;
    }
    const sec = sectionByKey(key.slice(4));
    if (sec) navigate(sec.path);
  };

  // Хвіст breadcrumb — назва поточного розділу, теж із маршруту.
  const title = scadaTitle(location.pathname);
  // ArtemK: у його меню розділ називається «Керування» (до 05.10 — «SCADA»),
  // тож і заголовок сторінки має бути такий самий, а не «Моніторинг
  // обладнання» — одна назва у двох місцях.
  // Топбар показує ЛИШЕ останній елемент breadcrumb.
  const isArtemK = hasArtemKView(user?.login);
  const crumbs = isArtemK && parseStationPath(location.pathname)
    ? [t('ak_nav_control')]
    : [t('d_bc_dispatch'), 'SCADA', ...(title ? [title] : [])];

  return (
    <DashboardLayout breadcrumb={crumbs}>
      {/* Full-bleed themed surface (світло/темна через дизайн-токени), Urbanist */}
      <div
        // ВАЖЛИВО: inline `margin`/`padding` перебивають класи md:-m-6 md:p-6, тож
        // на md+ вони ніколи не діяли (поверхня мала -16/16 замість -24/24, і
        // відступ від шапки виходив 24 = 24-16+16). Для ArtemK задаємо відступи
        // ЛИШЕ класами, щоб верх був 16px — як у ряду «Фінансів» (OptSectionTabs,
        // pt-4): обгортка каркаса дає p-4/md:p-6, мінусовий margin скасовує її,
        // далі рівно pt-4.
        style={{ background: scada.bg, minHeight: 'calc(100vh - 72px)', fontFamily: 'Urbanist, Inter, sans-serif',
          ...(isArtemK ? {} : { margin: -16, padding: 16 }) }}
        className={isArtemK ? '-m-4 p-4 md:-m-6 md:px-6 md:pb-6 md:pt-4' : 'md:-m-6 md:p-6'}>
        {/* Ідентифікація станції — той самий блок на ВСІХ сторінках SCADA.
            Для ArtemK прихований (рішення користувача 05.10): юридична назва
            підприємства та EIC йому в роботі не потрібні. Ховаємо РАЗОМ з
            обгорткою — порожній div із mb-4/6 лишив би дірку над вкладками. */}
        {!isArtemK && (
          <div className="mb-4 md:mb-6">
            <StationIdentity company={station.company} eic={telemetry?.eicW ?? cfg?.eicW} />
          </div>
        )}

        <div className="flex items-center" style={{ marginBottom: isArtemK ? 20 : 16 }}>
          <SegmentedTabs
            variant="outline"
            iconOnly
            items={items}
            activeKey={activeKey ?? ''}
            onSelect={onSelect}
          />
        </div>

        {/* Статус-смуга станції (точка приєднання, СЕС, PCS, УЗЕ, звʼязок) —
            на кожній вкладці картки станції, як на станційному HMI. На розділах
            рівня парку («Керування парком», «Події») її немає: там мова не про
            одну станцію. */}
        {fromUrl && telemetry && <StationStatusStrip stationId={fromUrl.id} t={telemetry} />}

        <Outlet />
      </div>
    </DashboardLayout>
  );
}
